/**
 * Calendar timezone utilities for live classes.
 * Classes are stored as date + time in America/Chicago (Central Time).
 * These helpers parse and format correctly for any user timezone.
 */
import { toDate, formatInTimeZone } from "date-fns-tz";

const CENTRAL_TIMEZONE = "America/Chicago";

/**
 * Parse a date and time string as Central Time, return a Date (UTC moment).
 * Handles DST correctly for America/Chicago regardless of user's timezone.
 *
 * @param {string} dateStr - "YYYY-MM-DD"
 * @param {string} timeStr - "HH:mm:ss" or "HH:mm"
 * @returns {Date} - Date representing that moment in UTC
 */
export function parseCentralDateTime(dateStr, timeStr = "00:00:00") {
  const dateTimeStr = `${dateStr}T${timeStr}`;
  return toDate(dateTimeStr, { timeZone: CENTRAL_TIMEZONE });
}

/**
 * Format a Date for display in a specific timezone.
 *
 * @param {Date} date - UTC moment
 * @param {string} timeZone - IANA timezone (e.g. "America/Chicago", "Australia/Sydney")
 * @returns {string} - "2:00 PM" format
 */
export function formatTimeInZone(date, timeZone) {
  return formatInTimeZone(date, timeZone, "h:mm a");
}

/**
 * Format a Date for display as date only in a specific timezone.
 *
 * @param {Date} date - UTC moment
 * @param {string} timeZone - IANA timezone
 * @returns {string} - "3/15/2024" format
 */
export function formatDateInZone(date, timeZone) {
  return formatInTimeZone(date, timeZone, "M/d/yyyy");
}

/**
 * Get day of week (short) in a specific timezone.
 *
 * @param {Date} date - UTC moment
 * @param {string} timeZone - IANA timezone
 * @returns {string} - "FRI", "SAT", etc.
 */
export function getDayOfWeekInZone(date, timeZone) {
  return formatInTimeZone(date, timeZone, "EEE").toUpperCase();
}

/**
 * Get month (short) in a specific timezone.
 *
 * @param {Date} date - UTC moment
 * @param {string} timeZone - IANA timezone
 * @returns {string} - "Mar", "Apr", etc.
 */
export function getMonthInZone(date, timeZone) {
  return formatInTimeZone(date, timeZone, "MMM");
}

/**
 * Get day of month in a specific timezone.
 *
 * @param {Date} date - UTC moment
 * @param {string} timeZone - IANA timezone
 * @returns {number} - 1-31
 */
export function getDayInZone(date, timeZone) {
  return parseInt(formatInTimeZone(date, timeZone, "d"), 10);
}

/**
 * Get the user's local timezone from the browser (Intl API).
 * Industry standard - no network requests, uses device timezone.
 *
 * @returns {string} - IANA timezone (e.g. "America/Chicago", "Australia/Sydney")
 */
export function getLocalTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
